import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Calendar, Download, Mail, Ruler, User } from 'lucide-react';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { exportCostumeSizingCSVAPI } from '~entities/recital-manager/api/export-costume-sizing-csv';
import { useGetCostumeSizings } from '~entities/recital-manager/api/get-costume-sizings';
import type { CostumeSizing } from '~entities/recital-manager/model/costume-sizing';

import {
  EnrollioTable,
  EnrollioTableSearch,
} from '~/shared/components/enrollio-table';
import { Badge } from '~/shared/shadcn/ui/badge';
import { Button } from '~/shared/shadcn/ui/button';
import { Card, CardContent } from '~/shared/shadcn/ui/card';

const columnHelper = createColumnHelper<CostumeSizing>();

interface CostumeSizingTabProps {
  recitalId: string;
}

export const CostumeSizingTab = ({ recitalId }: CostumeSizingTabProps) => {
  const { data: sizings = [], isLoading } = useGetCostumeSizings(recitalId);

  const handleExportCSV = async () => {
    try {
      await exportCostumeSizingCSVAPI(recitalId);
      toast.success('CSV exported successfully');
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (sizing) =>
          `${sizing.studentId?.firstName ?? ''} ${sizing.studentId?.lastName ?? ''}`.trim(),
        {
          id: 'student',
          header: 'Student',
          cell: ({ row }) => (
            <div className='flex items-center gap-2'>
              <User className='size-4 text-muted-foreground' />
              <span className='font-medium'>
                {row.original.studentId?.firstName ?? 'Unknown'}{' '}
                {row.original.studentId?.lastName ?? 'Student'}
              </span>
            </div>
          ),
        },
      ),
      columnHelper.accessor(
        (sizing) =>
          typeof sizing.performanceId === 'object'
            ? (sizing.performanceId.name ??
              sizing.performanceId.title ??
              'Unknown Performance')
            : (sizing.performanceId ?? 'Unknown Performance'),
        {
          id: 'performance',
          header: 'Performance',
          cell: (info) => <Badge variant='secondary'>{info.getValue()}</Badge>,
        },
      ),
      columnHelper.display({
        id: 'parent',
        header: 'Parent',
        cell: ({ row }) => {
          const parent =
            row.original.studentId?.parentId ?? row.original.parentId;
          return (
            <div>
              <div>
                {parent?.firstName ?? 'Unknown'} {parent?.lastName ?? 'Parent'}
              </div>
              <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                <Mail className='size-3' />
                {parent?.email ?? 'No email'}
              </div>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'sizes',
        header: 'Sizes',
        cell: ({ row }) => (
          <div className='flex flex-wrap gap-1'>
            {Object.entries(row.original.sizes)
              .filter(([, value]) => value && typeof value !== 'object')
              .map(([key, value]) => (
                <Badge key={key} variant='outline'>
                  {key}:{' '}
                  {typeof value === 'string' || typeof value === 'number'
                    ? value
                    : ''}
                </Badge>
              ))}
            {Object.entries(row.original.sizes.custom ?? {}).map(
              ([key, value]) => (
                <Badge key={key} variant='outline'>
                  {key}: {value}
                </Badge>
              ),
            )}
          </div>
        ),
      }),
      columnHelper.accessor('notes', {
        header: 'Notes',
        cell: (info) => info.getValue() ?? '—',
      }),
      columnHelper.accessor('submittedAt', {
        header: 'Submitted',
        cell: (info) => (
          <div className='flex items-center gap-1 text-muted-foreground'>
            <Calendar className='size-3' />
            {format(new Date(info.getValue()), 'MMM d, yyyy')}
          </div>
        ),
      }),
    ],
    [],
  );

  return (
    <div className='space-y-6'>
      <Card>
        <CardContent className='pt-6'>
          <EnrollioTable
            totalLabel='Costume Sizing Submissions'
            filtersLayout='inline'
            filterHeader={(table) => (
              <EnrollioTableSearch
                table={table}
                placeholder='Search submissions...'
              />
            )}
            toolbar={
              <Button onClick={handleExportCSV} variant='outline'>
                <Download className='mr-2 size-4' />
                Export CSV
              </Button>
            }
            data={sizings}
            columns={columns}
            getRowId={(row) => row._id}
            isUpdating={isLoading}
            emptyContent={
              <div className='flex flex-col items-center gap-2'>
                <Ruler className='size-8 text-muted-foreground' />
                Costume sizing submissions will appear here.
              </div>
            }
            ariaLabel='Costume sizing submissions'
          />
        </CardContent>
      </Card>
    </div>
  );
};
